import {DataResponse, ErrorCategory, StandardResponseData} from '../models/DataResponse';
import ConfigService from './ConfigService';
import UserService from './UserService';
import {isNil, merge} from 'lodash-es';
import {showErrorIfRequestFailed, showErrorMessage} from '../helpers/PopupMessageHelpers';
import {MagicLinkSession} from './MagicLinkSession';

export enum FetchOperation {
  Post = 'POST',
  Get = 'GET'
}

export default class DataService {

  private static readonly LOCAL_STORAGE_KEY = 'DSACCESSTOKEN';

  // Whether a session is currently established. The auth cookie is HttpOnly and so invisible to JavaScript;
  // this flag stands in for "is there a cookie" and is set once /authorisation/user confirms a session and
  // cleared on sign-out or a 401.
  private static _hasSession: boolean = false;

  /** Records whether a session is established. Set by UserService once /authorisation/user succeeds. */
  public static setSessionActive(active: boolean): void {
    DataService._hasSession = active;
  }

  public static clearToken() {
    DataService._hasSession = false;

    // The cookie is HttpOnly, so only the server can expire it. A bare fetch is used rather than
    // DataService.fetch because the wrapper signs out on 401, which would recurse straight back into here
    // when the token is already dead. Best-effort: the local session flag is already cleared and callers
    // navigate away regardless of the outcome.
    try {
      void fetch(`${ConfigService.config.backendUrl}/authorisation/logout`, {
        method: FetchOperation.Post,
        credentials: 'include',
        // Names which session to end - signing out of a link tab must leave the account session signed in.
        headers: {'Content-Type': 'application/json', ...MagicLinkSession.requestHeaders},
        body: '{}',
        // signOut() navigates (window.location.href = '/') immediately after this call, which aborts any
        // queued fetches. When other requests are in flight, the browser's connection cap keeps this POST
        // queued so it's canceled before reaching the server, the cookie is never expired, and the reload
        // logs the user straight back in. keepalive lets the request outlive the navigation.
        keepalive: true,
      }).catch(() => {
        // Best effort - nothing to recover if sign-out cannot reach the server.
      });
    } catch (ex) {
      // Intentionally left blank
    }

    try {
      localStorage.removeItem(DataService.LOCAL_STORAGE_KEY);
    } catch (ex) {
      // Intentionally left blank
    }
  }

  public static get isLoggedIn(): boolean {
    return DataService._hasSession;
  }

  /**
   * Fetches the current session's identity without the global 401 sign-out behaviour of {@link fetch}. A 401
   * here simply means "no session" - the ordinary boot state for a logged-out visitor - and must not trigger
   * the sign-out/redirect that fetch performs, which would reload the page and probe again in a loop.
   */
  public static async probeSession<T>(): Promise<T | null> {
    try {
      const response = await fetch(`${ConfigService.config.backendUrl}/authorisation/user`, {
        method: FetchOperation.Post,
        headers: {'Content-Type': 'application/json', ...MagicLinkSession.requestHeaders},
        credentials: 'include',
        body: '{}',
      });

      if (response.status !== 200) {
        return null;
      }

      return await response.json() as T;
    } catch (ex) {
      return null;
    }
  }

  private static extractErrorCategory(err: any): ErrorCategory {
    if (!isNil(err) && !isNil(err.errorCategory)) {
      return err.errorCategory;
    }
    if (!isNil(err) && !isNil(err.statusCode)) {
      return 'code';
    }
    return 'network';
  }

  public static async trySave(url: string,
                              requestBody?: any,
                              hideErrorMessages: boolean = false,
                              abortSignal?: AbortSignal): Promise<boolean> {

    try {
      let response = await this.fetch(url, requestBody, FetchOperation.Post, undefined, abortSignal);
      if (!hideErrorMessages) {
        showErrorIfRequestFailed(response);
      }
      return response.statusCode === 200;
    } catch (err) {

      if (!isNil(abortSignal) && abortSignal.aborted) {
        return false;
      }

      if (err.name === 'AbortError') {
        return false;
      }

      if (!hideErrorMessages) {
        showErrorMessage(DataService.extractErrorCategory(err));
      }

      return false;
    }
  }

  public static async tryLoadWithRetries<T = StandardResponseData>(url: string,
                                                                   requestBody?: any,
                                                                   hideErrorMessages: boolean = false,
                                                                   abortSignal?: AbortSignal): Promise<T | undefined> {
    for (let retries = 2; retries >= 0; retries--) {
      if (retries < 2) {
        await DataService.awaitDelay(3000);
      }

      try {
        let response = await this.fetch<T>(url, requestBody, FetchOperation.Post, undefined, abortSignal);
        if (retries <= 0) {
          if (!hideErrorMessages) {
            showErrorIfRequestFailed(response);
          }
        }
        if (response.statusCode === 200) {
          return response.data;
        }
      } catch (err) {
        if (!isNil(abortSignal) && abortSignal.aborted) {
          break;
        }

        if (err.name === 'AbortError') {
          break;
        }

        if (retries <= 0) {
          if (!hideErrorMessages) {
            showErrorMessage(DataService.extractErrorCategory(err));
          }
        }
      }
    }

    return undefined;
  }

  private static awaitDelay(milliseconds: number): Promise<void> {
    return new Promise(resolve => {
      setTimeout(resolve, milliseconds);
    });
  }

  public static async tryLoad<T = StandardResponseData>(url: string,
                                                        requestBody?: any,
                                                        hideErrorMessages: boolean = false,
                                                        abortSignal?: AbortSignal): Promise<T | undefined> {
    try {
      let response = await this.fetch<T>(url, requestBody, FetchOperation.Post, undefined, abortSignal);
      if (!hideErrorMessages) {
        showErrorIfRequestFailed(response);
      }
      if (response.statusCode === 200) {
        return response.data;
      } else {
        return undefined;
      }
    } catch (err) {
      if (!isNil(abortSignal) && abortSignal.aborted) {
        return undefined;
      }

      if (err.name === 'AbortError') {
        return undefined;
      }

      if (!hideErrorMessages) {
        showErrorMessage(DataService.extractErrorCategory(err));
      }
      return undefined;
    }
  }

  public static post<T>(url: string,
                        requestBody: unknown,
                        abortSignal: AbortSignal = null): Promise<T> {

    const requestUrl = ConfigService.config.backendUrl + url;

    const headers: Headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('crossorigin', 'true');
    MagicLinkSession.applyTo(headers);

    return new Promise<T>(async (resolve, reject) => {
      try {
        let response = await fetch(
          requestUrl,
          {
            method: FetchOperation.Post,
            headers: headers,
            body: JSON.stringify(requestBody),
            signal: abortSignal,
            credentials: 'include'
          }
        );
        const isJson = DataService.isJsonResponse(response);
        if (!isJson) {
          reject(new Error(`${url} : [${response.status}] Non-JSON response`));
          return;
        }

        const data = await response.json();

        if (response.status === 401) {
          UserService.instance.signOut();
          reject(new Error(`${url} : [401] Not authenticated`));
          return;
        }

        if (response.status !== 200) {
          reject(new Error(`${url} : [${response.status}] ${JSON.stringify(data)}`));
          return;
        }

        resolve(data as T);
      } catch (e) {
        if (!isNil(abortSignal) && abortSignal.aborted) {
          return reject(null);
        }
        reject(e);
      }
    });
  }

  public static async fetch<T = StandardResponseData>(url: string,
                                                      requestBody: unknown,
                                                      operation: FetchOperation = FetchOperation.Post,
                                                      confidentialData: unknown = null,
                                                      abortSignal: AbortSignal = null,
                                                      throwErrors: boolean = true): Promise<DataResponse<T>> {

    let baseUrl = ConfigService.config.backendUrl;
    let requestUrl = baseUrl + url;

    let headers: Headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('crossorigin', 'true');
    headers.append('X-Correlation-ID', UserService.instance.correlationId);
    MagicLinkSession.applyTo(headers);
    // headers.append('Access-Control-Allow-Origin', 'https://bn0ggf58.euw.devtunnels.ms:3000');
    // headers.append('Access-Control-Allow-Credentials', 'true');

    let allRequestData = {};
    merge(allRequestData, requestBody, confidentialData);

    return new Promise<DataResponse<T>>((resolve, reject) => {
      fetch(requestUrl, {
        method: operation.toString(),
        headers: headers,
        body: confidentialData ? JSON.stringify(allRequestData) : JSON.stringify(requestBody),
        signal: abortSignal,
        credentials: 'include'
      }).then((response: Response) => {
        const isJson = DataService.isJsonResponse(response);
        if (!isJson) {
          let dataResponse = new DataResponse<T>(null);
          dataResponse.response = response;
          dataResponse.statusCode = response.status;

          // A rejected access token is answered as text/html, so the 401 handling below never sees it. For an
          // ordinary session that is harmless - the page shows its own error and the user still has a
          // password to sign in with. A magic link recipient has neither, so their expired link is turned
          // into an explanation rather than left as a generic failure.
          if (response.status === 401 && MagicLinkSession.isActive) {
            UserService.instance.signOut();
            reject(dataResponse);
            return;
          }

          resolve(dataResponse);
          return;
        }

        response.json().then((data: T) => {
          let dataResponse = new DataResponse(data);
          dataResponse.response = response;

          if (response.status === 401) {
            UserService.instance.signOut();
            dataResponse.statusCode = response.status;
            reject(dataResponse);
          } else if (response.status === 400) {
            dataResponse.statusCode = response.status;
            resolve(dataResponse);
          } else if (response.status !== 200) {
            DataService.handleFetchError(operation, url, requestBody, response.status, null, throwErrors);
            dataResponse.statusCode = response.status;
            reject(dataResponse);
          } else {
            dataResponse.statusCode = response.status;
            resolve(dataResponse);
          }
        }).catch(reason => {
          DataService.handleFetchError(operation, url, requestBody, response.status, reason, throwErrors);
          reject(reason);
        });
      }).catch(() => {
        if (!isNil(abortSignal) && abortSignal.aborted) {
          reject();
          return null;
        }
        this.checkInternetAccess().then(isConnected => {
          const errorCategory: ErrorCategory = isConnected ? 'code' : 'network';

          let dataResponse = new DataResponse<T>(null);
          dataResponse.statusCode = isConnected ? 500 : 0;
          dataResponse.hasNetworkFailure = !isConnected;
          dataResponse.errorCategory = errorCategory;

          DataService.handleFetchError(
            operation,
            url,
            requestBody,
            500,
            isConnected ? 'Server is not responding' : 'Unable to connect to internet',
            throwErrors,
            errorCategory
          );

          reject(dataResponse);
        });
      });
    });
  }

  /**
   * An empty body - e.g. /telemetry/report-error's 200 - carries no Content-Type at all. Reading it as a string
   * would throw, and fetch's catch would then report the server as failing.
   */
  private static isJsonResponse(response: Response): boolean {
    return (response.headers.get('Content-Type') ?? '').startsWith('application/json');
  }

  public static async checkInternetAccess(): Promise<boolean> {
    if (window.navigator.onLine === false) {
      return false;
    }

    let response: Response;

    try {
      response = await fetch(`${ConfigService.config.backendUrl}/health`, {
        method: 'GET',
        cache: 'no-store'
      });
    } catch (ex) {
      // fetch() only rejects on true network failure (DNS, refused connection, etc.)
      return false;
    }

    return ![404, 502, 503, 504].includes(response.status);
  }

  public static async downloadBlob(url: string,
                                   mimeType: string,
                                   requestBody?: any,
                                   operation: FetchOperation = FetchOperation.Post)
    : Promise<{ blob: Blob, statusCode: number }> {

    let baseUrl = ConfigService.config.backendUrl;
    let requestUrl = baseUrl + url;

    let headers: Headers = new Headers();
    headers.append('Content-Type', 'application/json');
    MagicLinkSession.applyTo(headers);

    return new Promise<{ blob: Blob | undefined, statusCode: number }>((resolve, reject) => {

      let responseStatusCode: number = 500;

      fetch(requestUrl, {
        method: operation.toString(),
        headers: headers,
        body: JSON.stringify(requestBody || {}),
        credentials: 'include'
      }).then((response: Response) => {
        if (response.status !== 200) {
          reject({
            blob: undefined,
            statusCode: response.status
          });
          responseStatusCode = response.status;
          return undefined;
        } else {
          return response.blob();
        }
      }).then((file: Blob) => {
        let newBlob: Blob = new Blob([file], {type: mimeType});
        resolve({blob: newBlob, statusCode: 200});
      }).catch(() => {
        reject({
          blob: undefined,
          statusCode: responseStatusCode
        });
      });
    });
  }

  private static handleFetchError(operation: FetchOperation,
                                  url: string,
                                  requestBody: any, statusCode: number,
                                  errorReason: unknown,
                                  throwError: boolean,
                                  errorCategory: ErrorCategory = 'code') {
    if (statusCode < 500 || statusCode > 599) {
      return;
    }

    showErrorMessage(errorCategory).then();

    if (throwError) {
      if (errorReason instanceof Error) {
        throw errorReason;
      } else {
        const jsonBody = JSON.stringify(requestBody);

        throw new Error(
          `${operation} ${url} : [${statusCode}] ${errorReason || ''}${jsonBody === '{}' ? '' : ' - ' + jsonBody}`
        );
      }
    }
  }
}
